// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { tasksStrings } from './strings';
import type { TaskFilter } from './types';

export interface TaskFilterBarProps {
  readonly active: TaskFilter;
  readonly onChange: (filter: TaskFilter) => void;
}

const ALL_FILTERS: readonly TaskFilter[] = ['all', 'pending', 'completed'];

/** Three-chip horizontal filter bar (`all / pending / completed`). */
export const TaskFilterBar = React.memo<TaskFilterBarProps>(({ active, onChange }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const handlePress = useCallback(
    (filter: TaskFilter) => {
      if (filter !== active) {
        onChange(filter);
      }
    },
    [active, onChange],
  );

  return (
    <View
      style={[
        styles.bar,
        {
          backgroundColor: theme.colors.surface.primary,
          borderBottomColor: theme.colors.border.secondary,
        },
      ]}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {ALL_FILTERS.map((filter) => {
          const selected = filter === active;
          return (
            <Pressable
              key={filter}
              onPress={() => handlePress(filter)}
              accessibilityRole="button"
              accessibilityLabel={tasksStrings.filter(filter)}
              accessibilityState={{ selected }}
              style={({ pressed }) => [
                styles.chip,
                {
                  borderColor: selected
                    ? theme.colors.accent.indigo
                    : theme.colors.border.secondary,
                  backgroundColor: selected ? `${theme.colors.accent.indigo}1A` : 'transparent',
                  opacity: pressed ? 0.7 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.chipText,
                  {
                    color: selected ? theme.colors.accent.indigo : theme.colors.text.primary,
                  },
                ]}
              >
                {tasksStrings.filter(filter)}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
});

TaskFilterBar.displayName = 'TaskFilterBar';

export const TASK_FILTERS = ALL_FILTERS;

const styles = StyleSheet.create({
  bar: {
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  scrollContent: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    flexDirection: 'row',
  } satisfies ViewStyle,
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
    marginRight: 8,
  } satisfies ViewStyle,
  chipText: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
});
